// Equipos por retirar — Taller → Operación → Retiros (957).
// Avisos de abandono (30/60/90 días configurables), bodegaje acumulado y el
// flujo de declarar/reactivar un equipo abandonado. Sin ventanas modales:
// el panel de «Declarar abandonado» reemplaza la tabla con «Volver».
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, ArchiveRestore, Banknote, Bell, MessageCircle, PackageSearch, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { getErrorMessage } from '@/lib/api/client';
import { useTabsStore } from '@/stores/tabs.store';
import {
  Campo, fieldCls, money, useSort, SortTh, tallerRpc, QK, PanelIntegrado, useDialogoEnLinea, urlPitStop, urlWhatsApp, fmtFechaHora,
} from './shared';

interface Aviso { id: string; nivel: number; mensaje: string; generado_at: string; enviado_at: string | null; canal: string | null }
interface Bodegaje { dias_listo: number; dias_cobro: number; tarifa: number; monto: number; exonerado: boolean; configurado: boolean; desde_dia: number; abandono_dias: number }
interface RetiroFila {
  id: string; codigo: string; estado: 'listo' | 'abandonado'; cliente: string; celular: string | null; token: string; equipo: string; saldo: number;
  bodegaje: Bodegaje | null; abandonado_at: string | null; abandono_nota: string | null; avisos: Aviso[];
}

const CANALES: [string, string][] = [['llamada', 'Llamada'], ['correo', 'Correo'], ['presencial', 'Presencial'], ['sms', 'SMS']];

export function RetirosTaller() {
  const openTab = useTabsStore((s) => s.openTab);
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [abandonando, setAbandonando] = useState<string | null>(null);
  const { data = [], isLoading } = useQuery<RetiroFila[]>({
    queryKey: [QK, 'retiros'], queryFn: () => tallerRpc('retiros_listar'), refetchInterval: 30000,
  });
  const inv = () => void qc.invalidateQueries({ queryKey: [QK] });

  const sort = useSort<RetiroFila>(data, (r, k) => (k === 'codigo' ? r.codigo : k === 'cliente' ? r.cliente : k === 'equipo' ? r.equipo
    : k === 'dias' ? (r.bodegaje?.dias_listo ?? -1) : k === 'saldo' ? Number(r.saldo) : k === 'bodegaje' ? Number(r.bodegaje?.monto ?? 0) : null));

  const kpis = useMemo(() => ({
    porRetirar: data.filter((r) => r.estado === 'listo').length,
    conAvisos: data.filter((r) => r.avisos.some((a) => !a.enviado_at)).length,
    bodegaje: data.reduce((a, r) => a + (r.bodegaje && !r.bodegaje.exonerado ? Number(r.bodegaje.monto) : 0), 0),
    abandonados: data.filter((r) => r.estado === 'abandonado').length,
  }), [data]);

  const marcar = useMutation({
    mutationFn: (x: { aviso: string; canal: string }) => tallerRpc('aviso_marcar', { _aviso: x.aviso, _canal: x.canal }),
    onSuccess: () => { toast.success('Aviso marcado'); inv(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const exonerar = useMutation({
    mutationFn: (x: { orden: string; motivo: string }) => tallerRpc('bodegaje_exonerar', { _orden: x.orden, _motivo: x.motivo }),
    onSuccess: () => { toast.success('Bodegaje exonerado'); inv(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const reactivar = useMutation({
    mutationFn: (orden: string) => tallerRpc('orden_reactivar', { _orden: orden }),
    onSuccess: () => { toast.success('Equipo reactivado: listo para retirar'); inv(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });

  const abrirOrden = (id: string) => {
    sessionStorage.setItem('taller_abrir_orden', id);
    openTab({ id: '/taller/ordenes', title: 'Órdenes', path: '/taller/ordenes' });
  };
  const pedirExonerar = async (orden: string) => {
    const v = await dlg.pedir('Exonerar bodegaje', [{ key: 'motivo', label: 'Motivo', minimo: 5, placeholder: 'p. ej. cliente de confianza, demora nuestra' }], { aceptar: 'Exonerar' });
    if (v) exonerar.mutate({ orden, motivo: v.motivo });
  };

  const fila = abandonando ? data.find((r) => r.id === abandonando) : null;
  if (fila) return <DeclararAbandono fila={fila} onClose={() => setAbandonando(null)} onOk={() => { setAbandonando(null); inv(); }} />;

  return (
    <div className="space-y-3 p-4 sm:p-6">
      {dlg.ui}
      <h1 className="flex items-center gap-2 text-xl font-semibold"><PackageSearch className="h-5 w-5" /> Equipos por retirar</h1>
      <p className="text-xs text-muted-foreground">Los avisos (30/60/90 días, configurables) los genera el sistema cada mañana según los días que lleva listo cada equipo.</p>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi icon={<PackageSearch className="h-4 w-4" />} label="Por retirar" valor={kpis.porRetirar} />
        <Kpi icon={<Bell className="h-4 w-4" />} label="Con avisos pendientes" valor={kpis.conAvisos} destacar={kpis.conAvisos > 0} />
        <Kpi icon={<Banknote className="h-4 w-4" />} label="Bodegaje acumulado" valor={money(kpis.bodegaje)} destacar={kpis.bodegaje > 0} />
        <Kpi icon={<ShieldAlert className="h-4 w-4" />} label="Abandonados" valor={kpis.abandonados} destacar={kpis.abandonados > 0} peligro />
      </div>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pl-3 pr-2">N°</th>
              <SortTh col="codigo" sort={sort}>Orden</SortTh>
              <SortTh col="cliente" sort={sort}>Cliente</SortTh>
              <SortTh col="equipo" sort={sort}>Equipo</SortTh>
              <SortTh col="dias" sort={sort}>Días listo</SortTh>
              <SortTh col="saldo" sort={sort} className="text-right">Saldo</SortTh>
              <SortTh col="bodegaje" sort={sort} className="text-right">Bodegaje</SortTh>
              <th className="py-2 pr-2">Avisos</th>
              <th className="py-2 pr-3 text-right">Acciones</th>
            </tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">Cargando…</td></tr>}
              {!isLoading && sort.sorted.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">No hay equipos por retirar.</td></tr>}
              {sort.sorted.map((r, i) => {
                const b = r.bodegaje;
                const diasCls = r.estado === 'abandonado' ? '' : b && b.dias_listo >= b.abandono_dias ? 'font-semibold text-red-600' : b && b.dias_listo > 30 ? 'font-semibold text-amber-600' : '';
                const mensaje = `Hola ${r.cliente.split(' ')[0]}, su equipo está listo para retirar (orden ${r.codigo}). Siga su orden: ${urlPitStop(r.token)}`;
                return (
                  <tr key={r.id} className="border-b align-top last:border-0">
                    <td className="py-2 pl-3 pr-2 text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-2 font-mono font-semibold">{r.codigo}
                      {r.estado === 'abandonado' && <span className="ml-1 rounded bg-zinc-700 px-1.5 py-0.5 text-[10px] text-white">Abandonado</span>}</td>
                    <td className="py-2 pr-2">{r.cliente}<div className="text-[11px] text-muted-foreground">{r.celular ?? 'sin celular'}</div></td>
                    <td className="py-2 pr-2">{r.equipo}</td>
                    <td className={`py-2 pr-2 ${diasCls}`}>
                      {b ? `${b.dias_listo} día(s)` : r.abandonado_at ? `desde ${fmtFechaHora(r.abandonado_at)}` : '—'}
                    </td>
                    <td className="py-2 pr-2 text-right tabular-nums">{money(Number(r.saldo))}</td>
                    <td className="py-2 pr-2 text-right tabular-nums">
                      {b?.exonerado ? <span className="text-xs text-muted-foreground">Exonerado</span> : b ? money(Number(b.monto)) : '—'}
                    </td>
                    <td className="py-2 pr-2">
                      <div className="flex flex-col gap-1">
                        {r.avisos.length === 0 && <span className="text-xs text-muted-foreground">Sin avisos aún</span>}
                        {r.avisos.map((a) => (
                          <div key={a.id} className={`flex flex-wrap items-center gap-1 rounded px-1.5 py-0.5 text-[11px] ${a.enviado_at ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'}`}>
                            <span>Nivel {a.nivel}</span>
                            {a.enviado_at
                              ? <span>· {a.canal} · {fmtFechaHora(a.enviado_at)}</span>
                              : (
                                <>
                                  <span>· pendiente</span>
                                  <button type="button" title={a.mensaje}
                                    className="ml-1 inline-flex items-center gap-0.5 rounded bg-emerald-600 px-1 py-0.5 text-white"
                                    onClick={() => { window.open(urlWhatsApp(r.celular, mensaje), '_blank'); marcar.mutate({ aviso: a.id, canal: 'whatsapp' }); }}>
                                    <MessageCircle className="h-3 w-3" /> WhatsApp
                                  </button>
                                  <select className="ml-1 rounded border bg-background px-1 py-0.5 text-[11px]" value=""
                                    onChange={(e) => { if (e.target.value) marcar.mutate({ aviso: a.id, canal: e.target.value }); }}>
                                    <option value="">Marcar por…</option>
                                    {CANALES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                                  </select>
                                </>
                              )}
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex flex-wrap justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => abrirOrden(r.id)}>Abrir orden</Button>
                        {r.estado === 'listo' && b?.configurado && !b.exonerado && b.monto > 0 && (
                          <Button size="sm" variant="outline" onClick={() => void pedirExonerar(r.id)}>Exonerar bodegaje</Button>
                        )}
                        {r.estado === 'listo' && b && b.dias_listo >= b.abandono_dias && (
                          <Button size="sm" variant="destructive" onClick={() => setAbandonando(r.id)}><AlertTriangle className="mr-1 h-3.5 w-3.5" /> Declarar abandonado</Button>
                        )}
                        {r.estado === 'abandonado' && (
                          <Button size="sm" isLoading={reactivar.isPending} onClick={() => reactivar.mutate(r.id)}><ArchiveRestore className="mr-1 h-3.5 w-3.5" /> Reactivar</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {data.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold">
              <td colSpan={5} className="py-2 pl-3">Total: {data.length} equipo(s)</td>
              <td className="py-2 pr-2 text-right tabular-nums">{money(data.reduce((a, r) => a + Number(r.saldo), 0))}</td>
              <td className="py-2 pr-2 text-right tabular-nums">{money(kpis.bodegaje)}</td>
              <td colSpan={2} />
            </tr></tfoot>}
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function Kpi({ icon, label, valor, destacar, peligro }: { icon: React.ReactNode; label: string; valor: string | number; destacar?: boolean; peligro?: boolean }) {
  return (
    <Card className={destacar ? (peligro ? 'border-rose-300 dark:border-rose-800' : 'border-amber-300 dark:border-amber-800') : ''}>
      <CardContent className="p-3">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">{icon} {label}</div>
        <div className={`mt-1 text-xl font-semibold ${destacar ? (peligro ? 'text-rose-600' : 'text-amber-600') : ''}`}>{valor}</div>
      </CardContent>
    </Card>
  );
}

// ── Declarar abandonado ─────────────────────────────────────────────────────
function DeclararAbandono({ fila, onClose, onOk }: { fila: RetiroFila; onClose: () => void; onOk: () => void }) {
  const [nota, setNota] = useState('');
  const [acepto, setAcepto] = useState(false);
  const abandonar = useMutation({
    mutationFn: () => tallerRpc<{ aviso: string }>('orden_abandonar', { _orden: fila.id, _nota: nota, _acepto_legal: acepto }),
    onSuccess: (r) => { toast.success('Equipo declarado abandonado'); toast.warning(r.aviso, { duration: 10000 }); onOk(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <div className="space-y-3 p-4 sm:p-6">
      <PanelIntegrado title={`Declarar abandonado · ${fila.codigo}`} onClose={onClose} wide>
        <div className="space-y-3 text-sm">
          <div className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-rose-900 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-200">
            <div className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" /> Advertencia legal</div>
            <p className="mt-1">Antes de vender, reciclar o desechar el equipo consulte a un abogado. El Código Civil y la Ley Orgánica de Defensa del Consumidor (LODC) protegen la
              propiedad del cliente; los avisos enviados son la evidencia de las gestiones realizadas.</p>
          </div>
          <div className="text-xs text-muted-foreground">
            Cliente: <strong>{fila.cliente}</strong> · Equipo: <strong>{fila.equipo}</strong> · Días listo: <strong>{fila.bodegaje?.dias_listo ?? '—'}</strong>
          </div>
          <Campo label="Gestiones realizadas (mínimo 10 caracteres)">
            <textarea className={`${fieldCls} h-24`} placeholder="Llamadas, mensajes, visitas, fechas…" value={nota} onChange={(e) => setNota(e.target.value)} />
          </Campo>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={acepto} onChange={(e) => setAcepto(e.target.checked)} />
            Conozco la advertencia legal
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancelar</Button>
            <Button size="sm" variant="destructive" disabled={nota.trim().length < 10 || !acepto} isLoading={abandonar.isPending} onClick={() => abandonar.mutate()}>
              <ShieldAlert className="mr-1 h-4 w-4" /> Declarar abandonado
            </Button>
          </div>
        </div>
      </PanelIntegrado>
    </div>
  );
}
